import * as THREE from 'three';

import { Axial, axialKey, axialToWorld } from '../map/HexCoordinates';
import { HexCellData, waterLevel } from '../map/HexSurface';
import { HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { deckY } from '../map/RoadSurface';

/**
 * An X on the cell a route ends at.
 *
 * The line says which way; this says where, which is the part a line is worst
 * at — its far end is a point on a curve among many, and on a route running
 * away from the camera that point is the hardest thing on screen to place.
 *
 * Laid flat on the ground like the debug overlay rather than turned to face the
 * camera: it marks a cell, and a cell is a thing on the map.
 */

const COLOR = 0x9fd8ff;

/** Half the arm length, as a share of the hex radius. */
const REACH = 0.45;

/** Clear of the ground, like the route line it belongs to. */
const LIFT = 0.07;

export class TargetMark {
  readonly object: THREE.LineSegments;

  private cell?: Axial;

  constructor(private readonly world: Map<number, HexCellData>) {
    const material = new THREE.LineBasicMaterial({
      color: COLOR,
      transparent: true,
      opacity: 0.9,
      // Through hills, for the same reason the route is: it is a mark on a
      // plan, not something standing in the world.
      depthTest: false,
    });

    this.object = new THREE.LineSegments(new THREE.BufferGeometry(), material);
    this.object.renderOrder = 4;
    this.object.visible = false;
    this.object.frustumCulled = false;
  }

  showAt(cell: Axial): void {
    this.cell = cell;
    this.rebuild();
  }

  hide(): void {
    this.cell = undefined;
    this.object.visible = false;
  }

  /** Re-read the height, for when the ground under the mark streams in. */
  refresh(): void {
    if (this.cell) this.rebuild();
  }

  private rebuild(): void {
    if (!this.cell) return;

    const { x, z } = axialToWorld(this.cell.q, this.cell.r);
    const data = this.world.get(axialKey(this.cell.q, this.cell.r));
    // Pokład mostu, lustro na wodzie, a na lądzie wysokość pola — ta sama
    // kolejność pytań, co w `walkY`: X stawiany na morzu leżałby inaczej niż
    // trasa, która do niego prowadzi.
    const y =
      (data ? (deckY(data) ?? elevationToY(waterLevel(data) ?? data.elevation)) : 0) + LIFT;
    const arm = HEX_SIZE * REACH;

    this.object.geometry.dispose();
    this.object.geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x - arm, y, z - arm),
      new THREE.Vector3(x + arm, y, z + arm),
      new THREE.Vector3(x - arm, y, z + arm),
      new THREE.Vector3(x + arm, y, z - arm),
    ]);
    this.object.visible = true;
  }

  dispose(): void {
    this.object.geometry.dispose();
    (this.object.material as THREE.Material).dispose();
  }
}
